/**
 * Unit Count Panel - displays unit types and their counts
 * Enhanced with traditional Eastern aesthetic UI design
 * Positioned at bottom center like reference game
 */

import { useGameStore } from '@/state/gameStore';
import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { UnitComponent } from '@/game/components/Unit';
import type { FactionComponent } from '@/game/components/Faction';
import { injectSharedStyles } from '@/ui/styles/UIStyles';
import { UI_IMAGES } from '@/ui/styles/UIIcons';

interface UnitCounts {
  sword: number;
  archer: number;
  spear: number;
  cavalry: number;
  shield: number;
  taoist: number;
}

export class UnitCountPanel {
  private container: HTMLElement;
  private world: World | null = null;
  private unsubscribe: (() => void) | null = null;
  private updateInterval: number | null = null;

  constructor() {
    injectSharedStyles();

    this.container = document.createElement('div');
    this.container.id = 'unit-count-panel';
    this.container.innerHTML = `
      <style>
        #unit-count-panel {
          position: fixed;
          left: 16px;
          bottom: 16px;
          z-index: 10;
          font: 13px/1.4 'Noto Serif SC', 'SimSun', serif;
          color: #f5e6d3;
          user-select: none;
        }

        .unit-panel-wrapper {
          background:
            repeating-linear-gradient(
              92deg,
              transparent 0px,
              rgba(0,0,0,0.02) 1px,
              transparent 2px,
              transparent 5px
            ),
            repeating-linear-gradient(
              0deg,
              transparent 0px,
              rgba(139,90,43,0.08) 3px,
              transparent 6px,
              transparent 12px
            ),
            linear-gradient(145deg, #4a3728 0%, #2d1f15 50%, #3d2a1c 100%);
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 6px 20px rgba(0,0,0,0.6);
          padding: 0;
          overflow: hidden;
          position: relative;
        }

        /* Dragon corner ornaments - Using sprite sheet */
        .unit-panel-corner {
          position: absolute;
          width: 32px;
          height: 32px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 3;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .unit-panel-corner.tl { top: -5px; left: -5px; background-position: 0% 0%; }
        .unit-panel-corner.tr { top: -5px; right: -5px; background-position: 100% 0%; }
        .unit-panel-corner.bl { bottom: -5px; left: -5px; background-position: 0% 100%; }
        .unit-panel-corner.br { bottom: -5px; right: -5px; background-position: 100% 100%; }

        /* Title bar */
        .unit-panel-title-bar {
          background: linear-gradient(180deg,
            #6b4423 0%,
            #5a3a1e 25%,
            #4a2f17 50%,
            #5c3a20 75%,
            #6b4423 100%
          );
          border-bottom: 2px solid #8b6914;
          padding: 8px 12px;
          text-align: center;
          position: relative;
        }

        .unit-panel-title-bar::after {
          content: '';
          position: absolute;
          bottom: -3px;
          left: 20px;
          right: 20px;
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(212,175,55,0.6) 30%,
            rgba(212,175,55,0.8) 50%,
            rgba(212,175,55,0.6) 70%,
            transparent 100%
          );
        }

        .unit-panel-title {
          font: 700 13px/1 'Noto Serif SC', 'SimSun', serif;
          color: #ffd35a;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5), 0 0 8px rgba(255,180,0,0.2);
          letter-spacing: 2px;
        }

        /* Unit cards container */
        .unit-cards {
          display: flex;
          gap: 6px;
          padding: 12px;
          background: rgba(0,0,0,0.15);
        }

        /* Individual unit card */
        .unit-card {
          width: 60px;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          padding: 10px 6px 8px;
          text-align: center;
          transition: all 0.15s ease;
          position: relative;
          overflow: hidden;
        }

        .unit-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 100%);
          pointer-events: none;
        }

        .unit-card:hover {
          border-color: #d4af37;
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(0,0,0,0.4);
        }

        .unit-card.has-units {
          border-color: #8b6914;
        }

        /* Unit icon container */
        .unit-card-icon {
          width: 42px;
          height: 42px;
          margin: 0 auto 6px;
          background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
          border: 2px solid #5c4033;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .unit-card-icon svg {
          width: 28px;
          height: 28px;
        }

        .unit-card-icon img {
          width: 36px;
          height: 36px;
          object-fit: contain;
        }

        .unit-card.has-units .unit-card-icon {
          border-color: #8b6914;
          background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
        }

        /* Unit name */
        .unit-card-name {
          font-size: 11px;
          color: #9e8b6e;
          margin-bottom: 4px;
        }

        .unit-card.has-units .unit-card-name {
          color: #c4a77d;
        }

        /* Unit count */
        .unit-card-count {
          font-size: 14px;
          font-weight: 700;
          color: #6b5a45;
        }

        .unit-card.has-units .unit-card-count {
          color: #ffd700;
          text-shadow: 0 0 6px rgba(255,215,0,0.3);
        }

        /* Tooltip on hover */
        .unit-card::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(45,31,21,0.95);
          border: 1px solid #8b6914;
          border-radius: 4px;
          padding: 6px 10px;
          font-size: 11px;
          color: #f5e6d3;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          margin-bottom: 6px;
          box-shadow: 0 4px 8px rgba(0,0,0,0.4);
        }

        .unit-card:hover::after {
          opacity: 1;
        }

        /* Compact layout for small viewports (desktop 720p) */
        @media (max-height: 800px) {
          #unit-count-panel {
            left: 10px;
            bottom: 6px;
          }
          .unit-panel-wrapper {
            border-width: 2px;
          }
          .unit-panel-corner {
            width: 20px;
            height: 20px;
          }
          .unit-panel-title-bar {
            padding: 3px 10px;
          }
          .unit-panel-title {
            font-size: 10px;
            letter-spacing: 1px;
          }
          .unit-cards {
            gap: 3px;
            padding: 4px 6px;
          }
          .unit-card {
            width: 42px;
            padding: 4px 3px 3px;
          }
          .unit-card-icon {
            width: 28px;
            height: 28px;
            margin: 0 auto 2px;
            border-width: 1px;
          }
          .unit-card-icon svg {
            width: 20px;
            height: 20px;
          }
          .unit-card-icon img {
            width: 24px;
            height: 24px;
          }
          .unit-card-name {
            font-size: 9px;
            margin-bottom: 1px;
          }
          .unit-card-count {
            font-size: 11px;
          }
        }
      </style>

      <div class="unit-panel-wrapper">
        <div class="unit-panel-corner tl"></div>
        <div class="unit-panel-corner tr"></div>
        <div class="unit-panel-corner bl"></div>
        <div class="unit-panel-corner br"></div>

        <div class="unit-panel-title-bar">
          <div class="unit-panel-title">兵种统计</div>
        </div>

        <div class="unit-cards"></div>
      </div>
    `;

    document.body.appendChild(this.container);
    this.setupSubscription();
    this.startUpdateLoop();
    this.render();
  }

  setWorld(world: World): void {
    this.world = world;
  }

  private setupSubscription(): void {
    this.unsubscribe = useGameStore.subscribe(() => {
      this.render();
    });
  }

  private startUpdateLoop(): void {
    // Update every 500ms
    this.updateInterval = window.setInterval(() => {
      this.render();
    }, 500);
  }

  private getUnitCounts(): UnitCounts {
    const counts: UnitCounts = {
      sword: 0,
      archer: 0,
      spear: 0,
      cavalry: 0,
      shield: 0,
      taoist: 0,
    };

    if (!this.world) return counts;

    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return counts;

    // Count units for the player faction
    const entities = this.world.query([ComponentTypes.Unit, ComponentTypes.Faction]);

    for (const entity of entities) {
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);
      const unit = this.world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);

      if (faction?.factionId === playerFaction && unit) {
        const unitType = unit.unitType as keyof UnitCounts;
        if (unitType in counts) {
          counts[unitType]++;
        }
      }
    }

    return counts;
  }

  private render(): void {
    const cardsEl = this.container.querySelector('.unit-cards');
    if (!cardsEl) return;

    const counts = this.getUnitCounts();

    const unitTypes: Array<{ key: keyof UnitCounts; name: string; tooltip: string }> = [
      { key: 'sword', name: '剑士', tooltip: '近战步兵，克制道士' },
      { key: 'archer', name: '弓兵', tooltip: '远程单位，克制骑兵' },
      { key: 'spear', name: '枪兵', tooltip: '反骑兵，克制道士' },
      { key: 'cavalry', name: '骑兵', tooltip: '高机动性，克制枪兵' },
      { key: 'shield', name: '盾兵', tooltip: '高防御，克制弓兵' },
      { key: 'taoist', name: '道士', tooltip: '法术攻击，克制剑士' },
    ];

    cardsEl.innerHTML = unitTypes
      .map(
        ({ key, name, tooltip }) => `
      <div class="unit-card ${counts[key] > 0 ? 'has-units' : ''}" data-tooltip="${tooltip}">
        <div class="unit-card-icon"><img src="${UI_IMAGES.units[key]}" alt="${name}" /></div>
        <div class="unit-card-name">${name}</div>
        <div class="unit-card-count">×${counts[key]}</div>
      </div>
    `
      )
      .join('');
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    this.container.remove();
  }
}
